import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { push } from 'react-router-redux';
import moment from 'moment';

// Components
import ExtComp from '../../components/external';
import EmptyJobBlock from '../../components/EmptyJobs';

// Actions && services
import { updateJobsFilters } from '../../reducer/jobsActions';
import { getJobsList } from '../../services/jobRequests';
import { campusTitle } from '../../services/jobParsingData';

// External Actions
import { loadAndSavePractitionersList, updateCommonFilters } from 'src/views/practitioner/reducer/commonActions';

// Styles
import jobsStyle from '../../Jobs.style';
import s from './JobsList.style';


const JobList = (props) => {
  const { filters, dispatch, t, practitionersList, practitionerId } = props;
  const [jobsList, setJobsList] = useState([]);
  const [filterJobsList, setFilterJobsList] = useState([]);

  useEffect(() => {
    if (!practitionersList) {
      dispatch(loadAndSavePractitionersList());
    }
  }, []);

  useEffect(() => {
    if (practitionerId) {
      // load jobs if practitioner changed
      getJobsList(practitionerId)
        .then(jobsList => { setJobsList(jobsList); })
        .catch(() => { setJobsList([]); });
    }
  }, [practitionerId]);

  useEffect(() => { sortByDateRange(); }, [filters, jobsList]);

  const openJobDetails = (job) => {
    const jobType = job && job.type && job.type.toLowerCase();
    let redirectUrl;
    switch (jobType) {
      case 'communicate': {
        redirectUrl = 'communicate';
        break;
      }
      case 'reschedule': {
        redirectUrl = 'reschedule';
        break;
      }
      default:
        redirectUrl = 'jobs';
    }
    dispatch(push(`/practitioner/jobs/job-details/${redirectUrl}/${job.id}`));
  };

  const createNewJob = () => {
    dispatch(push('/practitioner/new-job'));
  };

  const onChangePractitioner = (practitioner) => {
    dispatch(updateCommonFilters({ practitionerId: practitioner.id }));
  };

  const sortByDateRange = () => {
    if (Array.isArray(jobsList)) {
      let filterArr = [];
      if (filters.startDate && filters.endDate) {
        filterArr = jobsList.filter(job =>
          moment(job.goalsDate).isSameOrAfter(moment(filters.startDate).startOf('day')) &&
          moment(job.goalsDate).isSameOrBefore(moment(filters.endDate).startOf('day'))
        );
      }
      if (filters.startDate && filters.endDate === null) {
        filterArr = jobsList.filter(job =>
          moment(job.goalsDate).isSameOrAfter(moment(filters.startDate).startOf('day'))
        );
      }
      setFilterJobsList(filterArr);
    } else {
      setFilterJobsList([]);
    }
  };

  return (
    <jobsStyle.JobGeneralWrapper >

      <ExtComp.InternalHeader />

      <s.JobsContentWrapper className='jobs-content'>

        <div className="screen-caption">
          <h1 className="screen-name">{t('list_jobs_screen_title')}</h1>


          <div className="filter-section">

            <ExtComp.PractitionersSelect
              practitioners={practitionersList}
              practitionerId={practitionerId}
              onChange={onChangePractitioner}
            />

            <div className="date-range">
              <ExtComp.DataPicker
                readOnly
                label={t('date_range')}
                selected={filters.startDate}
                selectsStart
                startDate={filters.startDate}
                endDate={filters.endDate}
                dateFormat="DD.MM.YYYY"
                onChange={date => {
                  if (filters.endDate && date.isSameOrAfter(filters.endDate)) {
                    dispatch(updateJobsFilters({ startDate: date.startOf('day'), endDate: null }));
                  } else {
                    dispatch(updateJobsFilters({ ...filters, startDate: date }));
                  }
                }}
              />

              <span className="delimiter">-</span>

              <ExtComp.DataPicker
                readOnly
                isClearable
                placeholderText={t('preferred_dates_in_to')}
                dateFormat="DD.MM.YYYY"
                selected={filters.endDate}
                selectsEnd
                startDate={filters.startDate && moment(filters.startDate).add(1, 'day')}
                endDate={filters.endDate}
                minDate={filters.startDate && moment(filters.startDate).add(1, 'day')}
                onChange={date => {
                  dispatch(updateJobsFilters({ ...filters, endDate: date }));
                }}
              />
            </div>

            <button className="action-btn" onClick={createNewJob}>
              {t('create_new_job_btn')}
            </button>
          </div>
        </div>


        {Array.isArray(filterJobsList) && filterJobsList.length ?
          <table className="jobs-table">
            <thead>
              <tr>
                <th>{t('job_created')}</th>
                <th>{t('job_type')}</th>
                <th>{t('app_ts_date')}</th>
                <th>{t('practitioner_and_location')}</th>
                <th>{t('appointments_short')}</th>
                <th>{t('job_states')}</th>
              </tr>
            </thead>

            <tbody>
              {filterJobsList.map(job =>
                <tr
                  key={`job-${job.id}`}
                  // className="error || active" for changing colors
                  onClick={() => { openJobDetails(job); }}
                >
                  <td>
                    {job.createdAt ? moment(job.createdAt).format('DD.MM.YYYY HH:mm') : t('no_data')}
                    <p className="sub-title">
                      {t('by')} {job.createdBy && job.createdBy.name}
                    </p>
                  </td>
                  <td>{t(job.type ? `job_type_${job.type.toLowerCase()}` : 'communication')}</td>
                  <td>{job.goalsDate ? moment(job.goalsDate).format('DD.MM.YYYY') : t('no_data')}</td>
                  <td>
                    {job.practitioner ?
                      `${job.practitioner.title} ${job.practitioner.firstName} ${job.practitioner.lastName}`
                      : t('no_data')
                    }
                    <p className="sub-title">
                      {campusTitle(job)}
                    </p>
                  </td>
                  <td>{String(job.countGoals) || t('no_data')}</td>
                  <td className={job.status && job.status.toLowerCase()}>{job.status || t('no_data')}</td>
                </tr>)
              }

            </tbody>
          </table>
          :
          <EmptyJobBlock title={t('jobs_table_empty_label')} />
        }

      </s.JobsContentWrapper>
    </jobsStyle.JobGeneralWrapper>
  );
};

const mapStateToProps = store => {
  const pr = store.practitioner;
  return {
    practitionerId: pr.common.commonFilters.practitionerId,
    practitionersList: pr.common.practitionersList,
    filters: pr.jobs.filters
  };
};

export default translate()(connect(
  mapStateToProps,
)(JobList)); 
